import React, { useState, useCallback } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { FormAutoComplete } from '../../form';
import {
    useGetClassificationTypeListQuery,
    useAssignContentsClassificationsMutation,
    GetContentDocument,
    GetContentQuery,
    GetContentQueryVariables,
} from '../../../generated/graphql';
import { gql, useApolloClient } from '@apollo/client';
import { ClipboardPaste, X, Check, AlertTriangle, ArrowRight, Info, Layers, RefreshCw } from 'lucide-react';
import styles from './MapClassificationDialog.module.css';

type ExistingClassification = {
    id: string;
    name: string;
    typeId: string;
    typeName: string;
};

type MappingRow = {
    /** Original pasted name */
    name: string;
    /** Original pasted category */
    category: string;
    /** Original pasted classification value */
    classificationValue: string;
    /** Matched content id from library (null if unmatched) */
    contentId: string | null;
    /** Matched content name from library */
    contentName: string | null;
    /** Resolved classification id (null if unmatched) */
    classificationId: string | null;
    /** Resolved classification name */
    classificationName: string | null;
    /** Existing classification from the same table that will be replaced (null if none) */
    existingClassification: ExistingClassification | null;
    /** All existing classification IDs on this content (for building the full list on apply) */
    allExistingClassificationIds: string[];
};

type MapClassificationDialogProps = {
    visible: boolean;
    libraryId: string;
    onClose: () => void;
};

const MapClassificationDialog = (props: MapClassificationDialogProps) => {
    const { visible, libraryId, onClose } = props;
    const apolloClient = useApolloClient();

    const [step, setStep] = useState<1 | 2>(1);
    const [selectedTypeId, setSelectedTypeId] = useState('');
    const [selectedTypeName, setSelectedTypeName] = useState('');
    const [pastedRows, setPastedRows] = useState<{ name: string; category: string; classificationValue: string }[]>([]);
    const [mappingRows, setMappingRows] = useState<MappingRow[]>([]);
    const [validationError, setValidationError] = useState('');
    const [duplicatesRemoved, setDuplicatesRemoved] = useState(0);
    const [previewLoading, setPreviewLoading] = useState(false);

    const [assignClassifications, assignResult] = useAssignContentsClassificationsMutation();

    // Fetch classification types for the dropdown
    const typesQuery = useGetClassificationTypeListQuery({
        fetchPolicy: 'no-cache',
        variables: { contains: '' },
    });

    const typeOptions = (typesQuery.data?.ClassificationTypeList?.nodes?.map(t => t?.name).filter(Boolean) as string[]) || [];

    const handleTypeChanged = (value: string) => {
        const selectedType = typesQuery.data?.ClassificationTypeList?.nodes?.find(t => t?.name === value) as any;
        if (selectedType) {
            setSelectedTypeId(selectedType.id);
            setSelectedTypeName(selectedType.name || '');
            setValidationError('');
        }
    };

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (!text.trim()) return;

            const lines = text.trim().split('\n');
            const headings = ['name', 'category', 'classification', 'type', 'title', 'class'];

            const parsedRows = lines
                .map(line => {
                    const parts = line.split('\t');
                    return {
                        name: (parts[0] || '').trim(),
                        category: (parts[1] || '').trim(),
                        classificationValue: (parts[2] || '').trim(),
                    };
                })
                .filter(r => {
                    if (!r.name) return false;
                    const nameLower = r.name.toLowerCase();
                    if (headings.includes(nameLower)) return false;
                    return true;
                });

            if (parsedRows.length === 0) return;

            // Deduplicate within pasted data (by name, case-insensitive)
            const seen = new Set<string>();
            let dupes = 0;
            const unique = parsedRows.filter(r => {
                const key = r.name.toLowerCase();
                if (seen.has(key)) { dupes++; return false; }
                seen.add(key);
                return true;
            });

            setDuplicatesRemoved(dupes);
            setPastedRows(unique);
            setValidationError('');
        } catch {
            setValidationError('Could not read clipboard. Please allow clipboard access.');
        }
    };

    const handleDeletePastedRow = (index: number) => {
        setPastedRows(prev => prev.filter((_, i) => i !== index));
    };

    const handleGeneratePreview = useCallback(async () => {
        if (!selectedTypeId) {
            setValidationError('Please select a Classification Table first.');
            return;
        }
        if (pastedRows.length === 0) {
            setValidationError('Please paste mapping data from the clipboard first.');
            return;
        }

        setPreviewLoading(true);
        setValidationError('');

        try {
            // Fetch ALL classifications for this type (paginated hooks only return the first page)
            const ALL_CLASSIFICATIONS_QUERY = gql`
                query getAllClassificationsForType($typeId: String) {
                    ClassificationList(first: 10000, where: { typeId: { eq: $typeId } }) {
                        nodes {
                            id
                            name
                        }
                    }
                }
            `;
            const classResult = await apolloClient.query<{
                ClassificationList?: { nodes?: Array<{ id?: string | null; name?: string | null } | null> | null };
            }>({
                query: ALL_CLASSIFICATIONS_QUERY,
                variables: { typeId: selectedTypeId },
                fetchPolicy: 'network-only',
            });
            const classificationNodes = classResult.data?.ClassificationList?.nodes || [];

            // Build classification lookup map
            const classificationMap = new Map<string, { id: string; name: string }>();
            for (const c of classificationNodes) {
                if (c?.name) {
                    classificationMap.set(c.name.toLowerCase(), { id: c.id || '', name: c.name });
                }
            }

            // Fetch ALL direct contents for this library (the table only has whatever pages were scrolled)
            const ALL_DIRECT_CONTENTS_QUERY = gql`
                query getAllDirectContents($libraryId: String, $after: String) {
                    Library(id: $libraryId) {
                        id
                        directContents(first: 1000, after: $after) {
                            nodes {
                                id
                                name
                            }
                            pageInfo {
                                hasNextPage
                                endCursor
                            }
                        }
                    }
                }
            `;
            type DirectContentsResult = {
                Library?: {
                    id?: string | null;
                    directContents?: {
                        nodes?: Array<{ id?: string | null; name?: string | null } | null> | null;
                        pageInfo?: { hasNextPage?: boolean | null; endCursor?: string | null } | null;
                    } | null;
                } | null;
            };

            const allContents: Array<{ id: string; name: string }> = [];
            let afterCursor: string | undefined = undefined;

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const vars: { libraryId: string; after: string | undefined } = { libraryId, after: afterCursor };
                const pageResult = await apolloClient.query<DirectContentsResult>({
                    query: ALL_DIRECT_CONTENTS_QUERY,
                    variables: vars,
                    fetchPolicy: 'network-only',
                });
                const nodes = pageResult.data?.Library?.directContents?.nodes || [];
                for (const n of nodes) {
                    if (n?.id && n?.name) {
                        allContents.push({ id: n.id, name: n.name });
                    }
                }
                const pageInfo = pageResult.data?.Library?.directContents?.pageInfo;
                if (!pageInfo?.hasNextPage) break;
                afterCursor = pageInfo?.endCursor ?? undefined;
            }

            // Build content lookup map (multiple contents can share the same name)
            const contentMap = new Map<string, Array<{ id: string; name: string }>>();
            for (const content of allContents) {
                const key = content.name.toLowerCase();
                const existing = contentMap.get(key);
                if (existing) {
                    existing.push({ id: content.id, name: content.name });
                } else {
                    contentMap.set(key, [{ id: content.id, name: content.name }]);
                }
            }

            // Find all matched content IDs so we can fetch their existing classifications
            const matchedContentIds: string[] = [];
            for (const row of pastedRows) {
                const matches = contentMap.get(row.name.toLowerCase());
                if (matches) {
                    for (const m of matches) matchedContentIds.push(m.id);
                }
            }

            // Fetch existing classifications for all matched contents
            const contentClassificationsMap = new Map<string, {
                classifications: Array<{ id: string; name: string; typeId: string; typeName: string }>;
                allClassificationIds: string[];
            }>();

            if (matchedContentIds.length > 0) {
                const batchSize = 10;
                for (let i = 0; i < matchedContentIds.length; i += batchSize) {
                    const batch = matchedContentIds.slice(i, i + batchSize);
                    const results = await Promise.all(
                        batch.map(id =>
                            apolloClient.query<GetContentQuery, GetContentQueryVariables>({
                                query: GetContentDocument,
                                variables: { id },
                                fetchPolicy: 'no-cache',
                            }).catch(() => null)
                        )
                    );

                    for (const result of results) {
                        const content = result?.data?.Content;
                        if (!content?.id) continue;

                        const classifications = (content.classifications || [])
                            .filter(Boolean)
                            .map(c => ({
                                id: c!.id || '',
                                name: c!.name || '',
                                typeId: c!.type?.id || '',
                                typeName: c!.type?.name || '',
                            }));

                        contentClassificationsMap.set(content.id, {
                            classifications,
                            allClassificationIds: classifications.map(c => c.id),
                        });
                    }
                }
            }

            const rows: MappingRow[] = pastedRows.flatMap((row): MappingRow[] => {
                const contentMatches = contentMap.get(row.name.toLowerCase()) || [];
                const classMatch = classificationMap.get(row.classificationValue.toLowerCase()) || null;

                // If no content matches, still produce one row so it shows as unmatched
                if (contentMatches.length === 0) {
                    return [{
                        name: row.name,
                        category: row.category,
                        classificationValue: row.classificationValue,
                        contentId: null,
                        contentName: null,
                        classificationId: classMatch?.id || null,
                        classificationName: classMatch?.name || null,
                        existingClassification: null,
                        allExistingClassificationIds: [],
                    }];
                }

                // One MappingRow per content that shares this name
                return contentMatches.map(contentMatch => {
                    let existingClassification: ExistingClassification | null = null;
                    let allExistingClassificationIds: string[] = [];

                    const contentData = contentClassificationsMap.get(contentMatch.id);
                    if (contentData) {
                        allExistingClassificationIds = contentData.allClassificationIds;
                        const existing = contentData.classifications.find(c => c.typeId === selectedTypeId);
                        if (existing) {
                            existingClassification = existing;
                        }
                    }

                    return {
                        name: row.name,
                        category: row.category,
                        classificationValue: row.classificationValue,
                        contentId: contentMatch.id,
                        contentName: contentMatch.name,
                        classificationId: classMatch?.id || null,
                        classificationName: classMatch?.name || null,
                        existingClassification,
                        allExistingClassificationIds,
                    };
                });
            });

            setMappingRows(rows);
            setStep(2);
        } catch {
            setValidationError('An error occurred while generating the preview.');
        } finally {
            setPreviewLoading(false);
        }
    }, [selectedTypeId, pastedRows, libraryId, apolloClient]);

    const matchedRows = mappingRows.filter(r => r.contentId && r.classificationId);
    const unmatchedRows = mappingRows.filter(r => !r.contentId || !r.classificationId);
    const replacingRows = matchedRows.filter(r => r.existingClassification !== null);
    const newAssignRows = matchedRows.filter(r => r.existingClassification === null);

    const handleApply = async () => {
        if (matchedRows.length === 0) {
            setValidationError('No matched rows to apply.');
            return;
        }

        setValidationError('');

        try {
            // Apply classification assignments one content at a time
            for (const row of matchedRows) {
                if (!row.contentId || !row.classificationId) continue;

                if (row.existingClassification) {
                    // This content already has a classification from this table —
                    // use overwrite: true with the full list, replacing the old one
                    const finalIds = [
                        ...row.allExistingClassificationIds.filter(id => id !== row.existingClassification!.id),
                        row.classificationId,
                    ];
                    await assignClassifications({
                        variables: {
                            libraryId,
                            ids: [row.contentId],
                            classificationIds: finalIds,
                            overwrite: true,
                        },
                    });
                } else {
                    // No existing classification from this table — safe to append
                    await assignClassifications({
                        variables: {
                            libraryId,
                            ids: [row.contentId],
                            classificationIds: [row.classificationId],
                            overwrite: false,
                        },
                    });
                }
            }
            onClose();
        } catch (err) {
            setValidationError('An error occurred while applying classifications.');
        }
    };

    const isLoading = typesQuery.loading || assignResult.loading || previewLoading;

    return (
        <DialogBase
            title={
                <span className="flex items-center gap-2">
                    <Layers size={18} />
                    Map Classification
                </span>
            }
            type="Custom"
            visible={visible}
            onClose={onClose}
            validationError={validationError}
            className={styles.MapClassificationDialog}
            width="900px"
            isLoading={isLoading}
            actions={
                step === 1 ? (
                    <>
                        <button className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleGeneratePreview}
                            disabled={!selectedTypeId || pastedRows.length === 0}
                        >
                            Preview Mapping <ArrowRight size={14} />
                        </button>
                    </>
                ) : (
                    <>
                        <button className="btn btn-secondary" onClick={() => setStep(1)}>
                            Back
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleApply}
                            disabled={matchedRows.length === 0}
                        >
                            Apply {matchedRows.length} Classification{matchedRows.length !== 1 ? 's' : ''}
                        </button>
                    </>
                )
            }
        >
            <div className={styles.stepsContainer}>
                {/* Step indicator */}
                <div className={styles.stepIndicator}>
                    <span className={`${styles.stepBadge} ${step === 1 ? styles.stepBadgeActive : styles.stepBadgeComplete}`}>
                        {step > 1 ? <Check size={13} /> : '1'}
                    </span>
                    <span className={`${styles.stepLabel} ${step === 1 ? styles.stepLabelActive : ''}`}>
                        Import Data
                    </span>
                    <span className={styles.stepDivider} />
                    <span className={`${styles.stepBadge} ${step === 2 ? styles.stepBadgeActive : styles.stepBadgeInactive}`}>
                        2
                    </span>
                    <span className={`${styles.stepLabel} ${step === 2 ? styles.stepLabelActive : ''}`}>
                        Preview &amp; Apply
                    </span>
                </div>

                {/* Step 1: Select type + paste */}
                {step === 1 && (
                    <div className={styles.stepContent}>
                        <div className={styles.typePickerSection}>
                            <FormAutoComplete
                                label="Classification Table"
                                name="classificationType"
                                value={selectedTypeName}
                                onChange={handleTypeChanged}
                                options={typeOptions}
                                placeholder="Select a classification table…"
                            />
                        </div>

                        <div className={styles.pasteSection}>
                            <div className={styles.pasteHint}>
                                <Info size={14} />
                                Copy a table with columns <strong>Name</strong>, <strong>Category</strong> &amp; <strong>Classification</strong> from a spreadsheet, then paste below.
                            </div>
                            <div
                                className={styles.pasteArea}
                                onClick={handlePasteFromClipboard}
                                style={{ cursor: 'pointer' }}
                            >
                                <ClipboardPaste size={28} className={styles.pasteAreaIcon} />
                                <span className={styles.pasteAreaText}>
                                    Click to paste from clipboard
                                </span>
                                <span className={styles.pasteAreaSubtext}>
                                    Tab-separated: Name → Category → Classification
                                </span>
                            </div>
                            {duplicatesRemoved > 0 && (
                                <div className={styles.duplicateWarning}>
                                    <AlertTriangle size={13} />
                                    {duplicatesRemoved} duplicate row{duplicatesRemoved !== 1 ? 's' : ''} removed.
                                </div>
                            )}
                        </div>

                        {/* Imported rows preview */}
                        {pastedRows.length > 0 && (
                            <div className={styles.importedPreview}>
                                <div className={styles.importedHeader}>
                                    <span className={styles.importedLabel}>Imported Data</span>
                                    <span className={styles.importedCount}>
                                        {pastedRows.length} row{pastedRows.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className={styles.importedTableContainer}>
                                    <table className={styles.importedTable}>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Category</th>
                                                <th>Classification</th>
                                                <th style={{ width: '36px' }} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pastedRows.map((row, i) => (
                                                <tr key={i}>
                                                    <td>{row.name}</td>
                                                    <td>{row.category}</td>
                                                    <td>{row.classificationValue}</td>
                                                    <td>
                                                        <button
                                                            className={styles.removeRowBtn}
                                                            onClick={() => handleDeletePastedRow(i)}
                                                            title="Remove row"
                                                        >
                                                            <X size={13} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2: Preview mapping */}
                {step === 2 && (
                    <div className={styles.stepContent}>
                        <div className={styles.previewSection}>
                            <div className={styles.infoBanner}>
                                <Info size={15} />
                                Mapping imported data onto library contents using <strong>{selectedTypeName}</strong> classifications.
                            </div>

                            {replacingRows.length > 0 && (
                                <div className={styles.replaceBanner}>
                                    <RefreshCw size={15} />
                                    <span>
                                        <strong>{replacingRows.length}</strong> content{replacingRows.length !== 1 ? 's' : ''} already {replacingRows.length !== 1 ? 'have' : 'has'} a <strong>{selectedTypeName}</strong> classification that will be <strong>replaced</strong>.
                                    </span>
                                </div>
                            )}

                            <div className={styles.previewHeader}>
                                <span className={styles.previewLabel}>Mapping Preview</span>
                                <div className={styles.previewStats}>
                                    {newAssignRows.length > 0 && (
                                        <span className={`${styles.previewStat} ${styles.statMatched}`}>
                                            <Check size={12} /> {newAssignRows.length} new
                                        </span>
                                    )}
                                    {replacingRows.length > 0 && (
                                        <span className={`${styles.previewStat} ${styles.statReplacing}`}>
                                            <RefreshCw size={12} /> {replacingRows.length} replacing
                                        </span>
                                    )}
                                    {unmatchedRows.length > 0 && (
                                        <span className={`${styles.previewStat} ${styles.statUnmatched}`}>
                                            <AlertTriangle size={12} /> {unmatchedRows.length} unmatched
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className={styles.previewTableContainer}>
                                <table className={styles.previewTable}>
                                    <thead>
                                        <tr>
                                            <th>Content Name</th>
                                            <th>Category</th>
                                            <th>Classification</th>
                                            <th>Existing</th>
                                            <th style={{ width: '110px' }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Rows that will replace existing */}
                                        {replacingRows.map((row, i) => (
                                            <tr key={`r-${i}`} className={styles.replacingRow}>
                                                <td>{row.contentName}</td>
                                                <td>{row.category}</td>
                                                <td>{row.classificationName}</td>
                                                <td>
                                                    <span className={styles.existingBadge}>
                                                        {row.existingClassification?.name}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`${styles.matchBadge} ${styles.matchBadgeReplacing}`}>
                                                        <RefreshCw size={11} /> Replacing
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        {/* New assignments (no existing) */}
                                        {newAssignRows.map((row, i) => (
                                            <tr key={`m-${i}`} className={styles.matchedRow}>
                                                <td>{row.contentName}</td>
                                                <td>{row.category}</td>
                                                <td>{row.classificationName}</td>
                                                <td>
                                                    <span className={styles.noneBadge}>—</span>
                                                </td>
                                                <td>
                                                    <span className={`${styles.matchBadge} ${styles.matchBadgeSuccess}`}>
                                                        <Check size={11} /> New
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Unmatched rows */}
                                        {unmatchedRows.map((row, i) => (
                                            <tr key={`u-${i}`} className={styles.unmatchedRow}>
                                                <td>{row.contentId ? row.contentName : row.name}</td>
                                                <td>{row.category}</td>
                                                <td>
                                                    {row.classificationId
                                                        ? row.classificationName
                                                        : <span style={{ fontStyle: 'italic' }}>{row.classificationValue}</span>
                                                    }
                                                </td>
                                                <td>
                                                    <span className={styles.noneBadge}>—</span>
                                                </td>
                                                <td>
                                                    <span className={`${styles.matchBadge} ${styles.matchBadgeWarning}`}>
                                                        <AlertTriangle size={11} />
                                                        {!row.contentId && !row.classificationId
                                                            ? 'No match'
                                                            : !row.contentId
                                                                ? 'No content'
                                                                : 'No class.'
                                                        }
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {mappingRows.length === 0 && (
                                    <div className="text-center py-10" style={{ color: 'var(--text-tertiary)' }}>
                                        No mapping data
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DialogBase>
    );
};

export default MapClassificationDialog;
